import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getProjects } from '../../services/notificationService';
import { useNotificationsPagination } from '../../hooks/useNotificationsPagination';
import NotificationFilters from './NotificationFilters';
import { CheckSquare, Square, MessageSquare, Bell, CheckCircle, Users } from 'lucide-react';
import { db } from '../../firebase/config';
import { doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

/* 
 * SYSTÈME DE NOTIFICATIONS/MESSAGES INDIVIDUALISÉ PAR UTILISATEUR
 * 
 * NOTIFICATIONS TIMELINE :
 * - 1 étape validée → 4 notifications créées (une pour chaque personne du dossier)
 * - Chaque utilisateur voit SEULEMENT ses propres notifications (userId = user.uid)
 * - Actions de lecture individuelles : chacun marque ses notifications comme lues
 * - Vignettes individuelles par utilisateur
 * 
 * MESSAGES/DISCUSSIONS :
 * - 1 discussion commune → Tous les participants du dossier
 * - 1 nouveau message → Tous les AUTRES participants voient "non lu"
 * - Statuts individuels : readBy: [userId1, userId2] - chacun marque pour lui-même
 * - Vignettes individuelles : chaque utilisateur voit seulement SES messages non lus
 */

const NotificationsOverview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Détecter le mode et les paramètres
  const searchParams = new URLSearchParams(location.search);
  const isMessagerieMode = location.pathname.includes('/messagerie');
  const pageType = isMessagerieMode ? 'messages' : 'timeline';
  const isArchivedFromUrl = location.pathname.includes('/archivees');
  const showArchived = isArchivedFromUrl;

  const initialReadStatus = searchParams.get('readStatus') || 'all';
  const initialFilters = {
    readStatus: initialReadStatus,
    project: searchParams.get('project') || 'all',
    dateRange: [null, null],
    startDate: '',
    endDate: '',
    // Pour les notifications : filtrer par userId
    // Pour les discussions : filtrer par participation
    userId: user?.uid,
    showArchived: showArchived // NOUVEAU: Inclure le statut archivé
  };

  // États du composant
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const [massActionLoading, setMassActionLoading] = useState(false);

  // Hook de pagination temps réel
  const {
    notifications = [],
    loading,
    initialLoading,
    error,
    hasMore,
    loadMoreNotifications,
    updateFilters,
    refreshNotifications,
    setNotifications
  } = useNotificationsPagination({
    ...initialFilters,
    deleted: showTrash,
    showArchived: showArchived, // NOUVEAU: Passer le statut archivé
    userId: user?.uid
  }, isMessagerieMode);

  // DEBUG: Logs pour diagnostiquer le problème
  console.log('🔍 OVERVIEW DEBUG: Utilisateur connecté:', user?.uid, user?.email);
  console.log('🔍 OVERVIEW DEBUG: Mode messagerie:', isMessagerieMode);
  console.log('🔍 OVERVIEW DEBUG: Notifications brutes reçues:', notifications?.length);
  console.log('🔍 OVERVIEW DEBUG: Première notification:', notifications?.[0]);

  // PLUS DE DOUBLE FILTRAGE ! Le hook fait déjà le filtrage côté client
  // On utilise directement les notifications du hook qui sont déjà filtrées
  const filteredNotifications = notifications.filter(notification => {
    console.log('🔍 FILTER DEBUG: Notification (filtrage minimal)', notification.id, {
      deleted: notification.deleted,
      archived: notification.archived,
      showTrash,
      showArchived
    });

    // SEULEMENT le filtrage par statut (trash/archived) car le reste est fait dans le hook
    if (showTrash) {
      return notification.deleted === true;
    } else if (showArchived) {
      return notification.archived === true && !notification.deleted;
    } else {
      return !notification.deleted && !notification.archived;
    }
  });

  console.log('🔍 OVERVIEW DEBUG: Notifications après filtrage:', filteredNotifications?.length);
  console.log('🔍 OVERVIEW DEBUG: Notifications filtrées:', filteredNotifications);

  // Chargement des projets
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        const projectsData = await getProjects();
        setProjects(projectsData);
      } catch (err) {
        console.error('Erreur lors du chargement des projets :', err);
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Rafraîchir quand on bascule entre normal et corbeille
  useEffect(() => {
    updateFilters({
      ...initialFilters,
      deleted: showTrash,
      showArchived: showArchived, // NOUVEAU: Inclure showArchived
      userId: user?.uid
    });
    setSelectedNotifications([]);
  }, [showTrash, showArchived, user?.uid]); // NOUVEAU: Dépendre aussi de showArchived

  // Observer pour le scroll infini
  const observer = useRef();
  const lastNotificationElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) loadMoreNotifications();
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, loadMoreNotifications]);

  // Gestion des filtres
  const handleApplyFilters = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.readStatus !== 'all') params.set('readStatus', newFilters.readStatus);
    if (newFilters.project !== 'all') params.set('project', newFilters.project);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    updateFilters({
      ...newFilters,
      deleted: showTrash,
      showArchived: showArchived, // NOUVEAU: Inclure showArchived
      userId: user?.uid
    });
  };

  // Gestion de la sélection
  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    }
  };

  // 🔧 FONCTION CORRIGÉE - Vérifier si une notification/discussion est lue pour l'utilisateur actuel
  const isReadByCurrentUser = (notification) => {
    if (isMessagerieMode) {
      // DISCUSSIONS : Vérifier si tous les messages individuels sont lus
      const messages = notification.messages || [];
      
      // S'il n'y a pas de messages, considérer comme lu
      if (messages.length === 0) {
        return true;
      }
      
      // Vérifier chaque message individuel
      const hasUnreadMessages = messages.some(message => {
        // Ignorer les messages créés par l'utilisateur actuel
        if (message.createdBy === user?.uid) {
          return false;
        }
        
        // Ignorer les messages automatiques (createdBy undefined)
        if (!message.createdBy) {
          return false;
        }
        
        // Vérifier si le message est lu par l'utilisateur actuel
        const readBy = message.readBy || [];
        return !readBy.includes(user?.uid);
      });
      
      // La discussion est "lue" seulement si AUCUN message individuel n'est non lu
      return !hasUnreadMessages;
    } else {
      // NOTIFICATIONS : Vérifier les champs lu/read (logique inchangée)
      return notification.lu || notification.read;
    }
  };

  // FONCTION - Marquer comme lu avec logique différente selon le type
  const handleMarkAsRead = async (notificationId) => {
    try {
      console.log('🔔 DÉBUT marquage comme lu pour:', notificationId, 'par utilisateur:', user?.uid);
      
      const notification = filteredNotifications.find(n => n.id === notificationId);
      if (!notification) {
        console.error('❌ Notification non trouvée:', notificationId);
        alert('Erreur: Notification non trouvée');
        return;
      }
      
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      const docRef = doc(db, collectionName, notificationId);
      
      // ÉTAPE 1: Mise à jour optimiste immédiate
      if (isMessagerieMode) {
        // DISCUSSIONS : Ajouter l'utilisateur à readBy de chaque message
        const updatedMessages = (notification.messages || []).map(message => {
          const readBy = message.readBy || [];
          if (message.createdBy !== user?.uid && message.createdBy && !readBy.includes(user.uid)) {
            return {
              ...message,
              readBy: [...readBy, user.uid]
            };
          }
          return message;
        });
        
        setNotifications(prevNotifications => 
          prevNotifications.map(notif => 
            notif.id === notificationId 
              ? { 
                  ...notif, 
                  messages: updatedMessages,
                  readBy: [...(notif.readBy || []), user.uid].filter((id, index, arr) => arr.indexOf(id) === index) // Éviter doublons
                }
              : notif
          )
        );
      } else {
        // NOTIFICATIONS : Marquer lu/read
        setNotifications(prevNotifications => 
          prevNotifications.map(notif => 
            notif.id === notificationId 
              ? { ...notif, lu: true, read: true, readAt: new Date() }
              : notif
          )
        );
      }
      
      console.log('✅ Mise à jour optimiste appliquée');
      
      // ÉTAPE 2: Mise à jour Firestore selon le type
      if (isMessagerieMode) {
        // DISCUSSIONS : Marquer chaque message individuel comme lu
        const messages = notification.messages || [];
        const updatedMessages = messages.map(message => {
          const readBy = message.readBy || [];
          if (message.createdBy !== user?.uid && message.createdBy && !readBy.includes(user.uid)) {
            return {
              ...message,
              readBy: [...readBy, user.uid]
            };
          }
          return message;
        });
        
        await updateDoc(docRef, {
          messages: updatedMessages,
          readBy: [...(notification.readBy || []), user.uid].filter((id, index, arr) => arr.indexOf(id) === index),
          lastReadAt: new Date()
        });
        
        console.log('✅ Discussion marquée comme lue - messages individuels mis à jour');
      } else {
        // NOTIFICATIONS : Marquer lu/read
        await updateDoc(docRef, {
          lu: true,
          read: true,
          readAt: new Date()
        });
        
        console.log('✅ Notification marquée comme lue');
      }
      
      // ÉTAPE 3: Déclencher le rafraîchissement des compteurs
      console.log('🔄 Déclenchement événement rafraîchissement compteurs...');
      window.dispatchEvent(new CustomEvent('notificationMarkedAsRead', { 
        detail: { 
          notificationId, 
          userId: user?.uid,
          type: isMessagerieMode ? 'discussion' : 'notification',
          timestamp: Date.now()
        }
      }));
      
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
        console.log('✅ Événements de rafraîchissement envoyés');
      }, 100);
      
    } catch (error) {
      console.error('❌ Erreur marquage comme lu:', error);
      refreshNotifications(); // Annuler la mise à jour optimiste
      alert('Erreur lors du marquage comme lu: ' + error.message);
    }
  };

  // ✅ NOUVELLE FONCTION : Voir le dossier ET marquer comme lu automatiquement
  const handleViewDossier = async (notification) => {
    try {
      console.log('📁 VOIR DOSSIER: Notification ID:', notification.id, 'Dossier:', notification.dossierId);
      
      // 1. Marquer comme lu SEULEMENT si pas déjà lu
      if (!isReadByCurrentUser(notification)) {
        console.log('📁 VOIR DOSSIER: Marquage automatique comme lu...');
        await handleMarkAsRead(notification.id);
      } else {
        console.log('📁 VOIR DOSSIER: Déjà marqué comme lu, navigation directe');
      }
      
      // 2. Naviguer vers le dossier
      console.log('📁 VOIR DOSSIER: Navigation vers:', `/admin/dossier/${notification.dossierId}`);
      navigate(`/admin/dossier/${notification.dossierId}`);
      
    } catch (error) {
      console.error('❌ VOIR DOSSIER: Erreur:', error);
      // En cas d'erreur de marquage, naviguer quand même
      navigate(`/admin/dossier/${notification.dossierId}`);
    }
  };

  // Marquer les sélectionnées comme lues
  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.length === 0) return;
    
    setMassActionLoading(true);
    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      const batch = writeBatch(db);
      
      // Filtrer pour ne marquer que les non lues
      const unreadSelected = filteredNotifications
        .filter(n => 
          selectedNotifications.includes(n.id) && 
          !isReadByCurrentUser(n)
        )
        .map(n => n.id);
      
      if (unreadSelected.length === 0) {
        alert('Toutes les sélections sont déjà marquées comme lues.');
        return;
      }
      
      // Mise à jour optimiste
      if (isMessagerieMode) {
        setNotifications(prevNotifications => 
          prevNotifications.map(notif => {
            if (unreadSelected.includes(notif.id)) {
              const updatedMessages = (notif.messages || []).map(message => {
                const readBy = message.readBy || [];
                if (message.createdBy !== user?.uid && message.createdBy && !readBy.includes(user.uid)) {
                  return {
                    ...message,
                    readBy: [...readBy, user.uid]
                  };
                }
                return message;
              });
              
              return { 
                ...notif, 
                messages: updatedMessages,
                readBy: [...(notif.readBy || []), user.uid].filter((id, index, arr) => arr.indexOf(id) === index)
              };
            }
            return notif;
          })
        );
      } else {
        setNotifications(prevNotifications => 
          prevNotifications.map(notif => 
            unreadSelected.includes(notif.id)
              ? { ...notif, lu: true, read: true, readAt: new Date() }
              : notif
          )
        );
      }
      
      // Batch update Firestore
      unreadSelected.forEach(id => {
        const notification = filteredNotifications.find(n => n.id === id);
        const docRef = doc(db, collectionName, id);
        
        if (isMessagerieMode) {
          const messages = notification.messages || [];
          const updatedMessages = messages.map(message => {
            const readBy = message.readBy || [];
            if (message.createdBy !== user?.uid && message.createdBy && !readBy.includes(user.uid)) {
              return {
                ...message,
                readBy: [...readBy, user.uid]
              };
            }
            return message;
          });
          
          const currentReadBy = notification.readBy || [];
          const newReadBy = [...currentReadBy, user.uid].filter((uid, index, arr) => arr.indexOf(uid) === index);
          batch.update(docRef, {
            messages: updatedMessages,
            readBy: newReadBy,
            lastReadAt: new Date()
          });
        } else {
          batch.update(docRef, {
            lu: true,
            read: true,
            readAt: new Date()
          });
        }
      });
      
      await batch.commit();
      
      console.log(`✅ ${unreadSelected.length} éléments marqués comme lus en lot`);
      setSelectedNotifications([]);
      
      window.dispatchEvent(new CustomEvent('notificationsBatchMarkedAsRead', { 
        detail: { 
          count: unreadSelected.length,
          userId: user?.uid,
          type: isMessagerieMode ? 'discussion' : 'notification',
          timestamp: Date.now()
        }
      }));
      
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
      }, 100);
      
      alert(`${unreadSelected.length} élément${unreadSelected.length > 1 ? 's' : ''} marqué${unreadSelected.length > 1 ? 's' : ''} comme lu${unreadSelected.length > 1 ? 's' : ''}`);
      
    } catch (error) {
      console.error('❌ Erreur marquage en lot:', error);
      refreshNotifications();
      alert('Erreur lors du marquage en lot: ' + error.message);
    } finally {
      setMassActionLoading(false);
    }
  };

  // Marquer TOUTES les non lues comme lues
  const handleMarkAllAsRead = async () => {
    const unreadNotifications = filteredNotifications.filter(n => !isReadByCurrentUser(n));
    
    if (unreadNotifications.length === 0) {
      alert('Tous les éléments sont déjà marqués comme lus.');
      return;
    }
    
    if (!window.confirm(`Marquer tous les ${unreadNotifications.length} éléments non lus comme lus ?`)) {
      return;
    }
    
    setMassActionLoading(true);
    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      const batch = writeBatch(db);
      
      // Mise à jour optimiste
      if (isMessagerieMode) {
        setNotifications(prevNotifications => 
          prevNotifications.map(notif => {
            if (!isReadByCurrentUser(notif)) {
              const updatedMessages = (notif.messages || []).map(message => {
                const readBy = message.readBy || [];
                if (message.createdBy !== user?.uid && message.createdBy && !readBy.includes(user.uid)) {
                  return {
                    ...message,
                    readBy: [...readBy, user.uid]
                  };
                }
                return message;
              });
              
              return { 
                ...notif, 
                messages: updatedMessages,
                readBy: [...(notif.readBy || []), user.uid].filter((id, index, arr) => arr.indexOf(id) === index)
              };
            }
            return notif;
          })
        );
      } else {
        setNotifications(prevNotifications => 
          prevNotifications.map(notif => 
            (!notif.lu && !notif.read && notif.userId === user?.uid)
              ? { ...notif, lu: true, read: true, readAt: new Date() } 
              : notif
          )
        );
      }
      
      unreadNotifications.forEach(notification => {
        const docRef = doc(db, collectionName, notification.id);
        
        if (isMessagerieMode) {
          const messages = notification.messages || [];
          const updatedMessages = messages.map(message => {
            const readBy = message.readBy || [];
            if (message.createdBy !== user?.uid && message.createdBy && !readBy.includes(user.uid)) {
              return {
                ...message,
                readBy: [...readBy, user.uid]
              };
            }
            return message;
          });
          
          const currentReadBy = notification.readBy || [];
          const newReadBy = [...currentReadBy, user.uid].filter((uid, index, arr) => arr.indexOf(uid) === index);
          batch.update(docRef, {
            messages: updatedMessages,
            readBy: newReadBy,
            lastReadAt: new Date()
          });
        } else {
          batch.update(docRef, {
            lu: true,
            read: true,
            readAt: new Date()
          });
        }
      });
      
      await batch.commit();
      
      console.log(`✅ Tous les ${unreadNotifications.length} éléments marqués comme lus`);
      setSelectedNotifications([]);
      
      window.dispatchEvent(new CustomEvent('notificationsAllMarkedAsRead', { 
        detail: { 
          count: unreadNotifications.length,
          userId: user?.uid,
          type: isMessagerieMode ? 'discussion' : 'notification',
          timestamp: Date.now()
        }
      }));
      
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
      }, 100);
      
      alert(`${unreadNotifications.length} élément${unreadNotifications.length > 1 ? 's' : ''} marqué${unreadNotifications.length > 1 ? 's' : ''} comme lu${unreadNotifications.length > 1 ? 's' : ''}`);
      
    } catch (error) {
      console.error('❌ Erreur marquage global:', error);
      refreshNotifications();
      alert('Erreur lors du marquage global: ' + error.message);
    } finally {
      setMassActionLoading(false);
    }
  };

  // Actions de suppression et restauration
  const handleDeleteNotifications = async (notificationIds) => {
    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      
      const updatePromises = notificationIds.map(id => 
        updateDoc(doc(db, collectionName, id), {
          deleted: true,
          deletedAt: new Date()
        })
      );
      
      await Promise.all(updatePromises);
      setSelectedNotifications([]);
      
      const itemType = isMessagerieMode ? 'discussion' : 'notification';
      alert(`${notificationIds.length} ${itemType}${notificationIds.length > 1 ? 's' : ''} déplacée${notificationIds.length > 1 ? 's' : ''} vers la corbeille`);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleRestoreNotifications = async (notificationIds) => {
    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      
      const updatePromises = notificationIds.map(id => 
        updateDoc(doc(db, collectionName, id), {
          deleted: false,
          deletedAt: null
        })
      );
      
      await Promise.all(updatePromises);
      setSelectedNotifications([]);
      
      const itemType = isMessagerieMode ? 'discussion' : 'notification';
      alert(`${notificationIds.length} ${itemType}${notificationIds.length > 1 ? 's' : ''} restaurée${notificationIds.length > 1 ? 's' : ''}`);
      
      // NOUVEAU: Événements spécifiques pour la restauration
      console.log('🔄 OVERVIEW: Déclenchement événements après restauration');
      window.dispatchEvent(new CustomEvent('discussionRestored', { 
        detail: { discussionIds: notificationIds } 
      }));
      
      setTimeout(() => {
        refreshNotifications();
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
      }, 200);
      
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      alert('Erreur lors de la restauration');
    }
  };

  const handlePermanentDelete = async (notificationIds) => {
    if (!window.confirm('Attention : Cette suppression est définitive et irréversible. Êtes-vous sûr de vouloir continuer ?')) {
      return;
    }

    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      
      const deletePromises = notificationIds.map(id => 
        deleteDoc(doc(db, collectionName, id))
      );
      
      await Promise.all(deletePromises);
      setSelectedNotifications([]);
      
      const itemType = isMessagerieMode ? 'discussion' : 'notification';
      alert(`${notificationIds.length} ${itemType}${notificationIds.length > 1 ? 's' : ''} supprimée${notificationIds.length > 1 ? 's' : ''} définitivement`);
    } catch (error) {
      console.error('Erreur lors de la suppression permanente:', error);
      alert('Erreur lors de la suppression permanente');
    }
  };

  const handleToggleTrash = () => {
    setShowTrash(!showTrash);
  };

  // Fonctions pour les discussions
  const handleArchiveDiscussion = async (notificationId) => {
    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      
      await updateDoc(doc(db, collectionName, notificationId), {
        archived: true,
        archivedAt: new Date()
      });
      
      alert('Discussion archivée avec succès');
      
      // NOUVEAU: Événements spécifiques pour l'archivage
      console.log('🔄 OVERVIEW: Déclenchement événements après archivage');
      window.dispatchEvent(new CustomEvent('discussionArchived', { 
        detail: { discussionId: notificationId } 
      }));
      
      setTimeout(() => {
        refreshNotifications();
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
      }, 200);
      
    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      alert('Erreur lors de l\'archivage de la discussion');
    }
  };

  const handleUnarchiveDiscussion = async (notificationId) => {
    try {
      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      
      await updateDoc(doc(db, collectionName, notificationId), {
        archived: false,
        archivedAt: null
      });
      
      alert('Discussion désarchivée avec succès');
      
      // NOUVEAU: Événements spécifiques pour le désarchivage
      console.log('🔄 OVERVIEW: Déclenchement événements après désarchivage');
      window.dispatchEvent(new CustomEvent('discussionRestored', { 
        detail: { discussionId: notificationId } 
      }));
      
      setTimeout(() => {
        refreshNotifications();
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
      }, 200);
      
    } catch (error) {
      console.error('Erreur lors du désarchivage:', error);
      alert('Erreur lors du désarchivage de la discussion');
    }
  };

  const handleMoveToTrash = async (notificationId) => {
    const notification = filteredNotifications.find(n => n.id === notificationId);
    const itemType = isMessagerieMode ? 'discussion' : 'notification';
    const confirmMessage = `Êtes-vous sûr de vouloir déplacer cette ${itemType} vers la corbeille ?\n\n"${notification?.title || itemType}"`;
    
    if (window.confirm(confirmMessage)) {
      try {
        const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
        
        await updateDoc(doc(db, collectionName, notificationId), {
          deleted: true,
          deletedAt: new Date()
        });
        
        alert(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} déplacée vers la corbeille`);
        
        // NOUVEAU: Événements spécifiques pour la suppression
        console.log('🔄 OVERVIEW: Déclenchement événements après suppression');
        window.dispatchEvent(new CustomEvent('discussionArchived', { 
          detail: { discussionId: notificationId } 
        }));
        
        setTimeout(() => {
          refreshNotifications();
          window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts'));
        }, 200);
        
      } catch (error) {
        console.error('Erreur lors du déplacement vers la corbeille:', error);
        alert('Erreur lors du déplacement vers la corbeille');
      }
    }
  };

  // Écouter les événements de rafraîchissement
  useEffect(() => {
    const handleExternalRefresh = () => {
      console.log('🔄 OVERVIEW: Rafraîchissement externe déclenché');
      refreshNotifications();
    };

    window.addEventListener('refreshNotifications', handleExternalRefresh);
    window.addEventListener('forceRefreshTimeline', handleExternalRefresh);
    
    return () => {
      window.removeEventListener('refreshNotifications', handleExternalRefresh);
      window.removeEventListener('forceRefreshTimeline', handleExternalRefresh);
    };
  }, [refreshNotifications]);

  // Fonction utilitaire pour la navigation forcée
  const handleForcedNavigation = (targetPath) => {
    console.log('🔄 Navigation forcée vers:', targetPath);
    // Force le rechargement complet de la page pour éviter les problèmes d'état
    window.location.href = targetPath;
  };
  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return '';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(date);
  };

  const toggleNotificationDetails = (notificationId) => {
    if (expandedNotification === notificationId) {
      setExpandedNotification(null);
    } else {
      setExpandedNotification(notificationId);
    }
  };

  const getTypeLabel = (notification) => {
    if (isMessagerieMode) {
      return 'Discussion';
    }
    
    switch (notification.type) {
      case 'timeline_step':
      case 'nouvelle_etape':
        return 'Nouvelle Étape';
      case 'timeline_update':
      case 'etape_validee':
        return 'Étape Validée';
      case 'project_status':
        return 'Statut Projet';
      case 'project_created':
        return 'Projet Créé';
      case 'nouveau_dossier':
        return 'Nouveau Dossier';
      case 'document_uploaded':
        return 'Document Ajouté';
      case 'system_alert':
        return 'Alerte Système';
      case 'dossier_cloture':
        return 'Dossier Clôturé';
      case 'deadline_proche':
        return 'Échéance Proche';
      default:
        return notification.type || 'Timeline';
    }
  };

  // Compter les non lues pour l'utilisateur actuel
  const unreadCount = Array.isArray(filteredNotifications)
    ? filteredNotifications.filter(n => !isReadByCurrentUser(n)).length
    : 0;

  const selectedUnreadCount = filteredNotifications
    .filter(n => selectedNotifications.includes(n.id) && !isReadByCurrentUser(n)).length;

  // Protection si pas d'utilisateur connecté
  if (!user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Connexion en cours...</div>
      </div>
    );
  }

  // Loading states
  if (initialLoading || projectsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Titre et icône selon le mode
  const getPageTitle = () => {
    if (showTrash) {
      return `Corbeille - ${isMessagerieMode ? 'Discussions' : 'Notifications Timeline'}`;
    } else if (showArchived && isMessagerieMode) {
      return 'Discussions Archivées';
    } else {
      return isMessagerieMode ? 'Messagerie' : 'Notifications Timeline';
    }
  };
  
  const pageTitle = getPageTitle();
  const PageIcon = isMessagerieMode ? MessageSquare : Bell;
  const iconColor = isMessagerieMode ? 'text-orange-600' : 'text-blue-600';

  return (
    <div className="bg-white shadow-md rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold flex items-center">
            <PageIcon className={`w-6 h-6 mr-2 ${iconColor}`} />
            {pageTitle}
            <span className="ml-2 text-sm text-gray-500">
              ({user?.displayName || user?.email})
            </span>
          </h2>
          {!showTrash && !showArchived && unreadCount > 0 && (
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {unreadCount} non {unreadCount === 1 ? 'lue' : 'lues'}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {!showTrash && !showArchived && !isMessagerieMode && (
            <Link
              to="/admin/notifications/nouvelle"
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Créer une notification
            </Link>
          )}
        </div>
      </div>

      {/* Barre d'actions de sélection multiple */}
      {!showTrash && filteredNotifications.length > 0 && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSelectAll}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {selectedNotifications.length === filteredNotifications.length ? (
                  <CheckSquare className="w-4 h-4 mr-1.5 text-blue-600" />
                ) : (
                  <Square className="w-4 h-4 mr-1.5" />
                )}
                {selectedNotifications.length === filteredNotifications.length ? 'Désélectionner tout' : 'Sélectionner tout'}
              </button>
              
              {selectedNotifications.length > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedNotifications.length} sélectionnée{selectedNotifications.length > 1 ? 's' : ''}
                  {selectedUnreadCount > 0 && (
                    <span className="text-amber-600 font-medium">
                      {' '}({selectedUnreadCount} non lue{selectedUnreadCount > 1 ? 's' : ''})
                    </span>
                  )}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {selectedNotifications.length > 0 && selectedUnreadCount > 0 && (
                <button
                  onClick={handleMarkSelectedAsRead}
                  disabled={massActionLoading}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {massActionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1.5"></div>
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                  )}
                  Marquer sélectionnées comme lues ({selectedUnreadCount})
                </button>
              )}
              
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={massActionLoading}
                  className="inline-flex items-center px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 disabled:opacity-50"
                >
                  {massActionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1.5"></div>
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                  )}
                  Marquer tout comme lu ({unreadCount})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filtres et actions */}
      <NotificationFilters
        onApplyFilters={handleApplyFilters}
        projects={projects}
        initialFilters={initialFilters}
        notifications={filteredNotifications}
        onDeleteNotifications={handleDeleteNotifications}
        onRestoreNotifications={handleRestoreNotifications}
        onPermanentDelete={handlePermanentDelete}
        selectedNotifications={selectedNotifications}
        onSelectNotification={handleSelectNotification}
        onSelectAll={handleSelectAll}
        showTrash={showTrash}
        onToggleTrash={handleToggleTrash}
      />

      {!showTrash && unreadCount > 0 && initialFilters.readStatus !== 'unread' && (
        <div className="mb-4 mt-3">
          <button
            onClick={() => handleApplyFilters({ ...initialFilters, readStatus: 'unread', userId: user?.uid })}
            className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Afficher uniquement les {unreadCount} non {unreadCount === 1 ? 'lue' : 'lues'}
          </button>
        </div>
      )}

      <div className="border-t border-gray-200 my-4"></div>

      {Array.isArray(filteredNotifications) && filteredNotifications.length === 0 ? (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                {showTrash 
                  ? `Aucune ${isMessagerieMode ? 'discussion' : 'notification timeline'} dans la corbeille.`
                  : `Aucune ${isMessagerieMode ? 'discussion' : 'notification timeline'} ne correspond à vos critères.`
                }
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 overflow-hidden">
            {filteredNotifications.map((notification, index) => {
              const projectName = projects.find(p => p.id === notification.projectId)?.name;
              const isLast = index === filteredNotifications.length - 1;
              const isRead = isReadByCurrentUser(notification);
              const isExpanded = expandedNotification === notification.id;
              const isSelected = selectedNotifications.includes(notification.id);
              
              const hasParticipants = notification.participantNames && Object.keys(notification.participantNames).length > 0;
              const participantsList = hasParticipants ? Object.values(notification.participantNames).join(', ') : null;
              
              return (
                <li
                  key={notification.id}
                  ref={isLast ? lastNotificationElementRef : null}
                  className={`${isRead ? '' : 'bg-blue-50'} ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                >
                  <div className={`block p-4 transition ${isRead ? 'hover:bg-gray-50' : 'hover:bg-blue-100'}`}>
                    <div className="flex items-start">
                      {/* Case à cocher */}
                      <button
                        onClick={() => handleSelectNotification(notification.id)}
                        className="flex-shrink-0 mr-3 mt-1"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>

                      {/* Icône selon le type */}
                      <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                        isRead ? 'bg-green-500' : (isMessagerieMode ? 'bg-orange-500' : 'bg-blue-500')
                      } text-white`}>
                        {isMessagerieMode ? (
                          <MessageSquare className="h-5 w-5" />
                        ) : (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                      </div>

                      <div className="ml-4 flex-1">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{notification.title}</h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                              isMessagerieMode 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {getTypeLabel(notification)}
                            </span>
                          </div>
                          <button 
                            onClick={() => toggleNotificationDetails(notification.id)}
                            className="text-blue-500 hover:text-blue-700 focus:outline-none"
                          >
                            {isExpanded ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            )}
                          </button>
                        </div>
                        
                        {/* Message */}
                        <p className={`mt-1 text-gray-600 ${isExpanded ? '' : 'line-clamp-2'}`}
                           style={{ whiteSpace: 'pre-line' }}>
                          {notification.message}
                        </p>
                        
                        {/* Participants si disponibles */}
                        {isExpanded && participantsList && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-md">
                            <h4 className="font-semibold text-gray-700 mb-1 flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              Participants
                            </h4>
                            <p className="text-gray-600">{participantsList}</p>
                          </div>
                        )}
                        
                        <div className="mt-2 text-sm text-gray-500 flex flex-wrap gap-2">
                          <span>{formatDate(notification.createdAt)}</span>
                          {projectName && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="text-blue-600">{projectName}</span>
                            </>
                          )}
                          <span className="text-gray-400">•</span>
                          <span className={isRead ? 'text-green-600' : 'text-amber-600 font-medium'}>
                            {isRead ? 'Lu' : 'Non lu'}
                          </span>
                          {showTrash && notification.deletedAt && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="text-red-600">
                                Supprimé le {formatDate(notification.deletedAt)}
                              </span>
                            </>
                          )}
                        </div>
                        
                        {/* Boutons d'action pour chaque notification */}
                        <div className="mt-3 flex space-x-2">
                          {showTrash ? (
                            // BOUTONS CORBEILLE : Restaurer et Supprimer définitivement
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestoreNotifications([notification.id]);
                                }}
                                className="inline-flex items-center px-2.5 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded-md hover:bg-green-100"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                </svg>
                                Restaurer
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePermanentDelete([notification.id]);
                                }}
                                className="inline-flex items-center px-2.5 py-1.5 bg-red-50 text-red-700 text-xs font-medium rounded-md hover:bg-red-100"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Supprimer définitivement
                              </button>
                            </>
                          ) : (
                            // BOUTONS NORMAUX : Actions habituelles
                            <>
                              {isMessagerieMode ? (
                                <button
                                  onClick={() => {
                                    console.log('🔥 CLICK sur discussion:', notification.dossierId, notification.id);
                                    const event = new CustomEvent('openDiscussion', { 
                                      detail: { dossierId: notification.dossierId, discussionId: notification.id } 
                                    });
                                    window.dispatchEvent(event);
                                  }}
                                  className="inline-flex items-center px-2.5 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-100"
                                >
                                  <MessageSquare className="w-3 h-3 mr-1" />
                                  Ouvrir la discussion
                                </button>
                              ) : (
                                notification.dossierId && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewDossier(notification);
                                    }}
                                    className="inline-flex items-center px-2.5 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-md hover:bg-blue-100"
                                  >
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 0a2 2 0 01-2-2V6a2 2 0 012-2h6a2 2 0 012 2v4a2 2 0 01-2 2m-6 0v6a2 2 0 002 2h4a2 2 0 002-2v-6" />
                                    </svg>
                                    Voir le dossier
                                  </button>
                                )
                              )}
                              
                              {isMessagerieMode && (
                                <>
                                  {showArchived ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarchiveDiscussion(notification.id);
                                      }}
                                      className="inline-flex items-center px-2.5 py-1.5 bg-green-50 text-green-700 text-xs font-medium rounded-md hover:bg-green-100"
                                    >
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l3-3m0 0l3 3m-3-3v9" />
                                      </svg>
                                      Désarchiver
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleArchiveDiscussion(notification.id);
                                        }}
                                        className="inline-flex items-center px-2.5 py-1.5 bg-yellow-50 text-yellow-700 text-xs font-medium rounded-md hover:bg-yellow-100"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8l4 4 4-4" />
                                        </svg>
                                        Archiver
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMoveToTrash(notification.id);
                                        }}
                                        className="inline-flex items-center px-2.5 py-1.5 bg-red-50 text-red-700 text-xs font-medium rounded-md hover:bg-red-100"
                                      >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Supprimer
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                              
                              {!isRead && (
                                <button
                                  className="inline-flex items-center px-2.5 py-1.5 bg-gray-50 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(notification.id);
                                  }}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Marquer comme lu
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {loading && !initialLoading && (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}

          {!hasMore && filteredNotifications.length > 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
              Vous avez atteint la fin de la liste
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsOverview;